import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { BellIcon, CheckCheckIcon, Loader2Icon, BanknoteIcon, TargetIcon, ShieldCheckIcon, Settings2Icon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataListToolbar } from '@/components/data/DataListToolbar'
import {
  PersistentListCard,
  type PersistentListCardEmptyProps,
} from '@/components/data/PersistentListCard'
import {
  getNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
  type PortalNotification,
  type NotificationsResponse,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { getNotificationRoute } from '@/lib/notification-routes'

const PAGE_SIZE = 20

// ── Category configuration ──────────────────────────────────────────────────

const CATEGORIES = [
  { key: null, label: 'All', icon: BellIcon, color: '' },
  { key: 'payment' as const, label: 'Payment', icon: BanknoteIcon, color: 'text-blue-500 bg-blue-500/10' },
  { key: 'circle' as const, label: 'Circle', icon: TargetIcon, color: 'text-purple-500 bg-purple-500/10' },
  { key: 'verification' as const, label: 'Verification', icon: ShieldCheckIcon, color: 'text-amber-500 bg-amber-500/10' },
  { key: 'system' as const, label: 'System', icon: Settings2Icon, color: 'text-muted-foreground bg-muted' },
]

/**
 * Infer a notification category from the message text.
 * Used as fallback for notifications created before the category column was added.
 */
function inferCategory(message: string, apiCategory: string | null): 'payment' | 'circle' | 'verification' | 'system' {
  if (apiCategory) return apiCategory as 'payment' | 'circle' | 'verification' | 'system'

  const lower = message.toLowerCase()
  if (lower.includes('payment') || lower.includes('paid') || lower.includes('cycle') || lower.includes('due') || lower.includes('overdue') || lower.includes('payout') || lower.includes('settled') || lower.includes('payer')) {
    return 'payment'
  }
  if (lower.includes('pot') || lower.includes('circle') || lower.includes('full') || lower.includes('funded') || lower.includes('participant') || lower.includes('joined') || lower.includes('request')) {
    return 'circle'
  }
  if (lower.includes('verification') || lower.includes('verified') || lower.includes('verify') || lower.includes('approve') || lower.includes('reject')) {
    return 'verification'
  }
  return 'system'
}

/** Category badge color map */
function categoryBadge(category: 'payment' | 'circle' | 'verification' | 'system'): string {
  switch (category) {
    case 'payment': return 'bg-blue-500/10 text-blue-600'
    case 'circle': return 'bg-purple-500/10 text-purple-600'
    case 'verification': return 'bg-amber-500/10 text-amber-600'
    case 'system': return 'bg-muted text-muted-foreground'
  }
}

/** Category icon */
function categoryIcon(category: 'payment' | 'circle' | 'verification' | 'system', className = 'size-5') {
  switch (category) {
    case 'payment': return <BanknoteIcon className={className} />
    case 'circle': return <TargetIcon className={className} />
    case 'verification': return <ShieldCheckIcon className={className} />
    case 'system': return <Settings2Icon className={className} />
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDateGroup(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diffMs = now - then
  const diffDay = Math.floor(diffMs / 86_400_000)

  if (diffDay === 0) return 'Today'
  if (diffDay === 1) return 'Yesterday'
  if (diffDay < 7) return `${diffDay} days ago`

  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatDetailedDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

// ── Route ──────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/dashboard/notifications')({
  component: () => {
    const [response, setResponse] = useState<NotificationsResponse | null>(null)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)
    const [markingAll, setMarkingAll] = useState(false)
    const [activeCategory, setActiveCategory] = useState<string | null>(null)
    const categoryScrollRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    const fetchData = useCallback(
      async (
        targetPage: number,
        isRefresh: boolean,
        signal?: { cancelled: boolean },
      ) => {
        if (signal?.cancelled) return
        if (isRefresh) setRefreshing(true)
        else setLoading(true)
        setFetchError(null)
        try {
          const data = await getNotificationsApi({ page: targetPage, limit: PAGE_SIZE })
          if (signal?.cancelled) return
          setResponse(data)
        } catch (err) {
          if (signal?.cancelled) return
          setFetchError(
            err instanceof Error ? err.message : 'Failed to load notifications',
          )
        } finally {
          if (!signal?.cancelled) {
            setLoading(false)
            setRefreshing(false)
          }
        }
      },
      [],
    )

    useEffect(() => {
      const signal = { cancelled: false }
      fetchData(page, false, signal)
      return () => {
        signal.cancelled = true
      }
    }, [page, fetchData])

    useEffect(() => {
      if (!response) return
      const newTotalPages = Math.max(1, Math.ceil(response.total / PAGE_SIZE))
      if (page > newTotalPages) setPage(1)
    }, [response, page])

    const rawNotifications: PortalNotification[] = response?.data ?? []
    const notifications = activeCategory
      ? rawNotifications.filter((n) => inferCategory(n.message, n.notification_category) === activeCategory)
      : rawNotifications
    const totalShown = notifications.length
    const totalUnfiltered = response?.total ?? 0
    const unreadCount = notifications.filter((n) => !n.is_read).length
    const initialLoaded = response !== null || fetchError !== null
    const isInitialLoad = loading && !initialLoaded

    const handleRefresh = () => fetchData(page, true)

    const handleMarkAllRead = async () => {
      setMarkingAll(true)
      try {
        await markAllNotificationsReadApi()
        setResponse((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.map((n) => ({ ...n, is_read: true })),
              }
            : prev,
        )
      } catch (err) {
        // silently fail
      } finally {
        setMarkingAll(false)
      }
    }

    const handleMarkRead = async (id: number) => {
      // Optimistic update
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              data: prev.data.map((n) =>
                n.id === id ? { ...n, is_read: true } : n,
              ),
            }
          : prev,
      )
      try {
        await markNotificationReadApi(id)
      } catch {
        // Revert on failure
        setResponse((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.map((n) =>
                  n.id === id ? { ...n, is_read: false } : n,
                ),
              }
            : prev,
        )
      }
    }

    // Build subtitle
    const subtitle = isInitialLoad ? (
      <span className="inline-block h-4 w-56 animate-pulse rounded bg-muted align-middle" />
    ) : totalUnfiltered === 0 && !fetchError ? (
      <>No notifications yet.</>
    ) : (
      <>
        <span className="font-medium text-foreground">{totalUnfiltered}</span>{' '}
        {totalUnfiltered === 1 ? 'notification' : 'notifications'}
        {activeCategory && totalShown !== totalUnfiltered && (
          <>
            {' · '}
            <span className="text-muted-foreground">{totalShown} shown</span>
          </>
        )}
        {unreadCount > 0 && (
          <>
            {' · '}
            <span className="font-medium text-[var(--brand-mint)]">
              {unreadCount}
            </span>{' '}
            unread
          </>
        )}
      </>
    )

    // Build empty state based on active filter
    const empty: PersistentListCardEmptyProps | null = activeCategory
      ? {
          icon: categoryIcon(activeCategory as 'payment' | 'circle' | 'verification' | 'system'),
          title: `No ${CATEGORIES.find((c) => c.key === activeCategory)?.label ?? activeCategory} notifications.`,
          description: `There are no ${(CATEGORIES.find((c) => c.key === activeCategory)?.label ?? activeCategory)}-related notifications to show.`,
        }
      : {
          icon: <BellIcon className="size-5" />,
          title: 'No notifications yet.',
          description:
            'Notifications about payment cycles, Funding Circle updates, and other activity will appear here.',
        }

    return (
      <div className="flex flex-col gap-6">
        <DataListToolbar
          title="Notifications"
          subtitle={subtitle}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          loading={loading}
          actions={
            unreadCount > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleMarkAllRead}
                disabled={markingAll}
              >
                {markingAll ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  <CheckCheckIcon className="size-3.5" />
                )}
                Mark all read
              </Button>
            ) : undefined
          }
        />

        {/* ── Category filter tabs ── */}
        <div ref={categoryScrollRef} className="overflow-x-auto -mx-1 px-1">
          <div className="flex gap-1.5 min-w-max pb-1">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.key
              const count = cat.key === null
                ? (response?.data?.length ?? 0)
                : (response?.data ?? []).filter((n) => inferCategory(n.message, n.notification_category) === cat.key).length

              return (
                <button
                  key={cat.key ?? 'all'}
                  type="button"
                  onClick={() => {
                    setActiveCategory(cat.key)
                    setPage(1)
                    // Scroll the active tab into view
                    categoryScrollRef.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
                  }}
                  aria-pressed={isActive}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all whitespace-nowrap',
                    isActive
                      ? 'bg-foreground text-background shadow-sm'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
                  )}
                >
                  <cat.icon className="size-3.5" />
                  {cat.label}
                  {count > 0 && (
                    <span className={cn(
                      'ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums',
                      isActive ? 'bg-background/20 text-inherit' : 'bg-background text-muted-foreground',
                    )}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <PersistentListCard<PortalNotification>
          loading={loading}
          initialLoaded={initialLoaded}
          error={fetchError}
          errorTitle="Couldn't load notifications"
          onRetry={handleRefresh}
          empty={empty}
          items={notifications}
          itemKey={(n) => n.id}
          page={page}
          pageSize={PAGE_SIZE}
          total={totalUnfiltered}
          onPageChange={setPage}
          pageLoading={loading}
          renderItem={(n) => {
            const cat = inferCategory(n.message, n.notification_category)
            const route = getNotificationRoute(cat)
            return (
              <div
                role={route ? 'button' : undefined}
                tabIndex={route ? 0 : undefined}
                onKeyDown={route ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!n.is_read) handleMarkRead(n.id); navigate({ to: route }) } } : undefined}
                onClick={route ? () => { if (!n.is_read) handleMarkRead(n.id); navigate({ to: route }) } : undefined}
                className={cn(
                  'flex items-start gap-4 rounded-xl border p-4 transition-all',
                  route && 'cursor-pointer',
                  n.is_read
                    ? 'border-border bg-card hover:bg-muted/30'
                    : 'border-[var(--brand-mint)]/30 bg-[var(--brand-mint)]/[0.04] hover:bg-[var(--brand-mint)]/[0.07]',
                )}
              >
                <div
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-lg',
                    n.is_read
                      ? 'bg-muted text-muted-foreground'
                      : categoryBadge(cat),
                  )}
                >
                  {categoryIcon(cat, 'size-5')}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-sm leading-snug',
                            n.is_read
                              ? 'text-muted-foreground'
                              : 'font-medium text-foreground',
                          )}
                        >
                          {n.message}
                        </span>
                        <span className="shrink-0 text-[10px] text-muted-foreground/40">
                          {formatDateGroup(n.scheduled_at || n.created_at)}
                        </span>
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                          categoryBadge(cat),
                        )}>
                          {categoryIcon(cat, 'size-3')}
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </span>
                        <span className="text-xs text-muted-foreground/60">
                          {formatDetailedDate(n.scheduled_at || n.created_at)}
                        </span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {!n.is_read && (
                        <span className="size-2 rounded-full bg-[var(--brand-mint)]" />
                      )}
                      {!n.is_read && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id) }}
                          className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <CheckCheckIcon className="size-3" />
                          Read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          }}
        />
      </div>
    )
  },
})
